import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { Dialog } from "@progress/kendo-react-dialogs";
import { label, notifyIcon, number, quote } from "../../../../config";
import DetailedFlowElementDetails from "../../../../shared/components/FlowElement/DetailedFlowElementDetails";
import { getNotification, isNonNegativeOrNull } from "../../../../utils/common";
import { FlowElementsContext } from "../../FlowElements/FlowElementsContextProvider";
import { WorkflowContext } from "../../WorkflowContextProvider";
import { updateNode, updateTaskNode } from "../ruleBasedFlow.service";
import AssignmentDisplay from "./AssignmentDisplay";
import "./NodeAssignment.scss";
import { SELF_ITEM } from "../../../../shared/components/FlowElement/FlowElementConstants";

const NodeAssignment = ({ nodeId, nodeData, onDeleteNode, onUpdateNodeData, isReadOnly = false, isCompleted = false }) => {
  const { workflowDefaults, workflow } = useContext(WorkflowContext);
  const { defaultDetails } = useSelector((state) => state.tasks);
  const { assignmentDefault } = useSelector((state) => state.taskSidebar);
  const { workflowId } = useParams();

  // In workflow context workflowId is present in URL params; in task context it is not
 
  const {taskId} = useSelector((state) => state.taskSidebar.task);
  const isTaskContext = taskId ;
  const taskAssignmentTypes = isTaskContext ? assignmentDefault?.taskAssignmentTypes : workflowDefaults?.taskAssignmentTypes;
  const workflowAssignmentTypes = isTaskContext ? assignmentDefault?.workflowAssignmentTypes : workflowDefaults?.workflowAssignmentTypes;

  const {
    flowAssignmentType,
    setFlowAssignmentType,
    flowTaskAssignmentId,
    setFlowTaskAssignmentId,
    flowWorkflowAssignmentType,
    setFlowWorkflowAssignmentType,
    flowWorkflowAssignmentId,
    setFlowWorkflowAssignmentId,
    flowElementDueDate,
    setFlowElementDueDate,
    flowElementDelay,
    setFlowElementDelay,
    isApproval,
    setIsApproval,
    isFlowLocked,
    setIsFlowLocked,
  } = useContext(FlowElementsContext);



  const [inEdit, setInEdit] = useState(false);
  const [assignmentData, setAssignmentData] = useState(nodeData || null);

  // Update local state when nodeData changes (from React Flow)
  useEffect(() => {
    const newAssignmentData = {
      inEdit: true,
      dueDateAfter: number.ZERO,
      delayAfterCompletion: number.ZERO,
      taskAssignmentType: number.THREE,
      workflowAssignmentType: number.FIVE,
      isApproval: number.ZERO,
      isFLowLocked: number.ZERO,
      dynamicFlow: false,
    };
    if (nodeData) {
      setAssignmentData(nodeData || null);
    }
    else {
      setAssignmentData(newAssignmentData);
    }

  }, [nodeData]);

  // Populate context when entering edit mode
  useEffect(() => {
    
    if (inEdit && assignmentData) {
      // Load common fields (for both dynamic and standard flows)
      if (assignmentData.dueDateAfter !== undefined) {
        setFlowElementDueDate(assignmentData.dueDateAfter);
      }

      if (assignmentData.delayAfterCompletion !== undefined) {
        setFlowElementDelay(assignmentData.delayAfterCompletion);
      }

      if (assignmentData.isApproval !== undefined) {
        setIsApproval(assignmentData.isApproval);
      }

      if (assignmentData.isFlowLocked !== undefined) {
        setIsFlowLocked(assignmentData.isFlowLocked);
      }

      // Load standard flow specific fields (only if not dynamic)
      if (!assignmentData.dynamicFlow && assignmentData.flowElementType !== 2) {
        if (assignmentData.taskAssignmentType) {
          const assignmentType = taskAssignmentTypes?.find((t) => t.value === assignmentData.taskAssignmentType);
          if (assignmentType) {
            setFlowAssignmentType(assignmentType);
          }
        }

        if (assignmentData.taskAssignmentId) {
          setFlowTaskAssignmentId(assignmentData.taskAssignmentId);
        }

        if (assignmentData.workflowAssignmentType) {
          const workflowType = workflowAssignmentTypes?.find((t) => t.value === assignmentData.workflowAssignmentType);
          if (workflowType) {
            setFlowWorkflowAssignmentType(workflowType);
          }
        }

        if (assignmentData.workflowAssignmentId) {
          setFlowWorkflowAssignmentId(assignmentData.workflowAssignmentId);
        }
      }
    }
  }, [inEdit, assignmentData, taskAssignmentTypes, workflowAssignmentTypes]);

  const isRuleBasedFlow = true;
  const flowElement = assignmentData
  const dataItem = {
    inEdit: inEdit,
    isApproval: isApproval !== undefined ? isApproval : (flowElement?.isApproval || false),
    isFlowLocked: isFlowLocked !== undefined ? isFlowLocked : (flowElement?.isFlowLocked || false),
    nodeId: nodeId,
    taskAssignmentType: flowTaskAssignmentId?.AssignmentType ?? flowElement?.taskAssignmentType,
    taskAssignmentId: flowTaskAssignmentId?.value ?? flowElement?.taskAssignmentId,
    workflowAssignmentType: flowWorkflowAssignmentId?.RelatedAssignmentType ?? flowElement?.workflowAssignmentType,
    workflowAssignmentId: flowWorkflowAssignmentId?.value ?? flowElement?.workflowAssignmentId,
    dueDateAfter: flowElementDueDate !== undefined ? flowElementDueDate : flowElement?.dueDateAfter,
    delayAfterCompletion: flowElementDelay !== undefined ? flowElementDelay : flowElement?.delayAfterCompletion,
    dynamicFlow: flowElement?.dynamicFlow,
    flowElementType: flowElement?.flowElementType,
    variableId: flowElement?.variableId,
    assignBasedOnCreator: flowElement?.assignBasedOnCreator,
    selectedActiveMember: flowElement?.selectedActiveMember,
    profileType: flowElement?.profileType,
  };

  /**
   * Validate required fields for the flow element
   * @param {boolean} isDynamicFlow - Whether this is a dynamic flow
   * @returns {boolean} - Whether all required fields are valid
   */
  const requiredFields = (isDynamicFlow) => {
    if (isDynamicFlow) {
      return isNonNegativeOrNull(flowElementDueDate) && isNonNegativeOrNull(flowElementDelay);
    } else {
      const projectWorkflowOrUser =
        flowTaskAssignmentId?.AssignmentType === number.THREE 
          ? (flowWorkflowAssignmentId === null || (flowWorkflowAssignmentId?.RelatedAssignmentType && (flowWorkflowAssignmentId.value || flowWorkflowAssignmentId.label === label.UNASSIGNED))) 
          : true;

      return (
        flowTaskAssignmentId?.AssignmentType &&
        flowTaskAssignmentId &&
        projectWorkflowOrUser &&
        isNonNegativeOrNull(flowElementDueDate) &&
        isNonNegativeOrNull(flowElementDelay)
      );
    }
  };

  /**
   * Calculate assignment project ID based on assignment type
   */
  const getAssignmentProjectId = () => {
    if (flowTaskAssignmentId?.AssignmentType === number.THREE) {
      return flowTaskAssignmentId?.value; // Project selected directly
    } else {
      // Find user's personal project
      return defaultDetails?.allProjectsList?.find((item) => item.IsPersonal === number.ONE && item.CreatedBy === flowTaskAssignmentId?.value)
        ?.ProjectId;
    }
  };

  /**
   * Create payload for flow element
   * @param {object} dataItem - The data item being edited
   * @returns {object} - The payload for API
   */
  const createPayload = (dataItem) => {
    const payload = dataItem.dynamicFlow
      ? {
          variableId: dataItem?.selectedActiveMember?._id,
          flowElementType: 2,
          dueDateAfter: flowElementDueDate,
          delayAfterCompletion: flowElementDelay,
          sortId: 0, // Rule-based flow doesn't use sortId
          isApproval,
          isFlowLocked,
          assignBasedOnCreator: dataItem?.profileType === "ticketCreator" ? 1 : 0,
          selectedActiveMember: dataItem?.selectedActiveMember,
          dynamicFlow: true,
          profileType: dataItem?.profileType,
          toSelf : dataItem?.profileType === "ticketCreator" && dataItem?.selectedActiveMember?.name === SELF_ITEM.name ? 1 : 0,
        }
      : {
          flowElementType: 1,
          taskAssignmentType: flowTaskAssignmentId?.AssignmentType,
          taskAssignmentId: flowTaskAssignmentId?.value,
          workflowAssignmentType: flowWorkflowAssignmentId?.RelatedAssignmentType ,
          workflowAssignmentId: flowWorkflowAssignmentId?.value ,
          dueDateAfter: flowElementDueDate,
          delayAfterCompletion: flowElementDelay,
          sortId: 0, // Rule-based flow doesn't use sortId
          assignmentProjectId: getAssignmentProjectId(),
          isApproval,
          isFlowLocked,
          dynamicFlow: false,
        };

    return payload;
  };

  /**
   * Handle save/update of flow element
   * Called when user clicks the tick/save button
   */
  const handleSaveFlowElement = async (dataItem) => {
    // Validate required fields
    if (!requiredFields(dataItem.dynamicFlow)) {
      getNotification(quote.REQUIRED_FIELDS_MISSING || "Please fill all required fields", notifyIcon.WARNING_ICON);
      return;
    }

    try {
      // Create the flow element payload
      const flowElementPayload = createPayload(dataItem);

      // Prepare the full payload for updateNode API
      const payload = {
        nodeId: nodeId,
        workflowId: workflowId || workflow?.Id,
        ...(isTaskContext ? { taskId: taskId } : {}),
        data: {
          workflowElements: [flowElementPayload],
          isNewTask: false,
        },
      };

      // Call the API
      const response =  isTaskContext ? await updateTaskNode(payload) : await updateNode(payload);
      if (response) {
        // Prepare assignment data for display and node state update
        // Different data structure for dynamic vs standard flows
        const updatedAssignmentData = dataItem.dynamicFlow
          ? {
              // Dynamic Flow: Store variable and profile info
              flowElementType: 2,
              dynamicFlow: true,
              variableId: dataItem?.selectedActiveMember?._id,
              assignBasedOnCreator: dataItem?.profileType === "ticketCreator" ? 1 : 0,
              selectedActiveMember: dataItem?.selectedActiveMember,
              profileType: dataItem?.profileType,
              dueDateAfter: flowElementDueDate,
              delayAfterCompletion: flowElementDelay,
              isApproval,
              isFlowLocked,
              toSelf : dataItem?.profileType === "ticketCreator" && dataItem?.selectedActiveMember?.name === SELF_ITEM.name ? 1 : 0,

            }
          : {
              // Standard Flow: Store task and workflow assignment info
              flowElementType: 1,
              dynamicFlow: false,
              taskAssignmentType: flowTaskAssignmentId?.AssignmentType,
              taskAssignmentId: flowTaskAssignmentId?.value,
              workflowAssignmentType: flowWorkflowAssignmentId?.RelatedAssignmentType ,
              workflowAssignmentId: flowWorkflowAssignmentId?.value ,
              dueDateAfter: flowElementDueDate,
              delayAfterCompletion: flowElementDelay,
              isApproval,
              isFlowLocked,
            };

        // Store assignment data locally for display
        setAssignmentData(updatedAssignmentData);
       
        // Update the node's data in React Flow state
        if (onUpdateNodeData) {
          onUpdateNodeData(nodeId, updatedAssignmentData);
        }

        // Close the dialog
        setInEdit(false);

        // Show success notification
        getNotification("Assignment updated successfully", notifyIcon.SUCCESS_ICON);
      } else {
        getNotification("Failed to update assignment", notifyIcon.ERROR_ICON);
      }
    } catch (error) {
      console.error("Error updating node assignment:", error);
      getNotification("Error updating assignment", notifyIcon.ERROR_ICON);
    }
  };

  /**
   * Handle cancel/discard of flow element edit
   * Just closes the dialog without saving
   */
  const handleCancelFlowElement = () => {
    setInEdit(false);
  };

  const handleEnterEditMode = () => {
    setInEdit(true);
  };

  const handleExitEditMode = () => {
    setInEdit(false);
  };

  return (
    <>
      {/* Always show assignment display */}
      <AssignmentDisplay setInEdit={setInEdit} onDeleteNode={onDeleteNode} nodeId={nodeId} assignmentData={assignmentData} isReadOnly={isReadOnly} isCompleted={isCompleted} />

      {/* Show popup when in edit mode */}
      {inEdit && (
        <Dialog title='Edit Assignment' onClose={handleExitEditMode} width={650} height={260} className='node-assignment-dialog p-0'>
          <div className='dialog-content'>
            <DetailedFlowElementDetails
              taskAssignmentTypes={taskAssignmentTypes}
              workflowAssignmentTypes={workflowAssignmentTypes}
              dataItem={dataItem}
              editField='inEdit'
              isRuleBasedFlow={isRuleBasedFlow}
              onEnterEditMode={handleEnterEditMode}
              onExitEditMode={handleExitEditMode}
              onDeleteNode={onDeleteNode}
              handleFlowElementEdit={handleSaveFlowElement}
              handleNewFlowElement={handleSaveFlowElement}
              handleDeleteFlowElement={() => {}}
            />
          </div>
        </Dialog>
      )}
    </>
  );
};

export default NodeAssignment;
